/**
 * Landing App
 *
 * Public-facing routes: homepage, search, listings, auth
 */

import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { queryClient } from "./lib/queryClient";
import { ToastContainer } from "./components/common/Toast";
import { ErrorBoundary } from "./components/common/ErrorBoundary";

// Layouts
import { PublicLayout } from "./layouts/PublicLayout";
import { AuthLayout } from "./layouts/AuthLayout";

// Pages
import HomePage from "./pages/HomePage";
import SearchPage from "./pages/SearchPage";
import ListingDetailPage from "./pages/ListingDetailPage";
import LoginPage from "./pages/auth/LoginPage";
import SignupPage from "./pages/auth/SignupPage";
import MagicLinkPage from "./pages/auth/MagicLinkPage";
import NotFoundPage from "./pages/NotFoundPage";

// Redirect component for dashboard routes
const RedirectToDashboard = () => {
  const dashboardUrl = import.meta.env.VITE_DASHBOARD_URL || window.location.origin;
  const currentPath = window.location.pathname + window.location.search;

  // In development, same origin - show notice
  if (dashboardUrl === window.location.origin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-mist">
        <div className="text-center p-8 max-w-md">
          <h1 className="text-xl font-bold text-ink mb-4">Development Mode</h1>
          <p className="text-slate-600 mb-4">
            You&apos;re running the landing app but trying to access dashboard routes.
          </p>
          <p className="text-sm text-slate-500 mb-4">
            To access dashboard pages in development, restart with:
          </p>
          <code className="block bg-slate-100 p-2 rounded text-sm mb-4">
            npm run dev:dashboard
          </code>
          <a href="/" className="text-accent hover:underline">
            ← Back to Home
          </a>
        </div>
      </div>
    );
  }

  window.location.href = `${dashboardUrl}${currentPath}`;
  return null;
};

const LandingApp = () => (
  <QueryClientProvider client={queryClient}>
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          {/* Public routes with header */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/listing/:id" element={<ListingDetailPage />} />
          </Route>

          {/* Auth routes */}
          <Route element={<AuthLayout />}>
            <Route path="/auth/login" element={<LoginPage />} />
            <Route path="/auth/signup" element={<SignupPage />} />
            <Route path="/auth/magic-link" element={<MagicLinkPage />} />
          </Route>

          {/* Dashboard routes redirect */}
          <Route path="/dashboard/*" element={<RedirectToDashboard />} />
          <Route path="/admin/*" element={<RedirectToDashboard />} />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
      <ToastContainer />
    </ErrorBoundary>
  </QueryClientProvider>
);

export default LandingApp;
